import { Reveal } from "../lib/reveal";
import { Building2, Truck, Factory, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const WASTEWATER_IMG =
  "https://images.unsplash.com/photo-1582669300365-630322b86cf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZmFjaWxpdHklMjB3YXN0ZXdhdGVyfGVufDB8fHx8MTc4NTI0MDE4NHww&ixlib=rb-4.1.0&q=85";

const segments = [
  {
    icon: Building2,
    tag: "Primary",
    title: "Municipal Corporations",
    body: "Urban local bodies, smart cities and sanitation departments facing citizen complaints, NGT pressure and bio-mining mandates.",
    need: "Reduce odour immediately while improving landfill conditions.",
  },
  {
    icon: Truck,
    tag: "Secondary",
    title: "Waste Operators & Bio-mining",
    body: "PPP operators, remediation firms and EPC contractors who need faster project completion and cleaner compliance.",
    need: "Improve landfill project execution efficiency.",
  },
  {
    icon: Factory,
    tag: "Tertiary",
    title: "Industrial Organic Waste",
    body: "Poultry, dairy, fisheries, food processing, slaughterhouses and wastewater plants under pollution-board pressure.",
    need: "Eliminate odour and improve decomposition economically.",
  },
];

export const Industries = () => {
  const { t } = useLang();
  return (
    <section id="industries" data-testid="industries-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("Who we serve", "हम किसकी सेवा करते हैं")}</span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink">
                {t("Built for every generator of organic waste.", "प्रत्येक जैविक अपशिष्ट उत्पादक के लिए बनाया गया।")}
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Left: image tile */}
          <Reveal className="md:col-span-5">
            <div className="relative h-full min-h-[320px] rounded-3xl overflow-hidden border border-stone/20">
              <img src={WASTEWATER_IMG} alt="Aerial view of industrial wastewater treatment tanks" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/10 to-transparent" />
              <div className="absolute bottom-0 p-8 text-bone">
                <p className="font-serif text-3xl leading-tight">4,000+ urban local bodies.<br />Hundreds of legacy sites.</p>
                <p className="text-sm text-bone/70 mt-3 tracking-wide">A nationwide remediation opportunity.</p>
              </div>
            </div>
          </Reveal>

          {/* Right: segment cards */}
          <div className="md:col-span-7 grid grid-cols-1 gap-5">
            {segments.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="group bg-bone rounded-3xl border border-stone/20 p-8 hover:bg-sand transition-colors duration-400 flex flex-col md:flex-row gap-6">
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-full bg-moss/10 flex items-center justify-center group-hover:bg-moss transition-colors duration-400">
                      <s.icon size={24} strokeWidth={1.5} className="text-moss group-hover:text-bone transition-colors duration-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[0.68rem] tracking-[0.2em] uppercase text-terracotta font-semibold">{s.tag} Market</span>
                      <ArrowUpRight size={20} className="text-stone opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-400" />
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-1 mb-3 text-ink">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-slate">{s.body}</p>
                    <p className="mt-4 text-sm italic font-serif text-moss">“{s.need}”</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
